import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Tag, TrendingUp, ArrowUpDown, Gem, Flame, Wheat, Wrench } from "lucide-react";
import { SortableHeader, applySorting } from "@/components/SortableHeader";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  precious_metals: Gem,
  energy: Flame,
  agriculture: Wheat,
  industrial_metals: Wrench,
};

const CATEGORY_COLORS = {
  precious_metals: "text-yellow-400",
  energy: "text-orange-400",
  agriculture: "text-green-400",
  industrial_metals: "text-blue-400",
};

export const CommoditiesTab = ({ saveToMyDeals, isDemo, t }) => {
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const fetchCommodities = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/prices/commodity/batch/all`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCommodities(data.commodities || []);
      }
    } catch (err) {
      toast.error("Failed to fetch commodities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCommodities(); }, []);

  const filtered = commodities.filter(
    (c) => categoryFilter === "all" || c.category === categoryFilter
  );

  const SORT_GETTERS = {
    name: (c) => c.name,
    category: (c) => c.category,
    price: (c) => c.price,
    risk_score: (c) => c.risk_score,
  };

  const sorted = applySorting(filtered, sort, SORT_GETTERS);

  const categories = [...new Set(commodities.map((c) => c.category))];

  const handleSave = (commodity) => {
    saveToMyDeals({
      name: commodity.name,
      description: `${commodity.name} (${commodity.symbol}) — ${commodity.unit} unit. Category: ${commodity.category.replace("_", " ")}`,
      price: commodity.price,
      risk_score: commodity.risk_score,
      expected_return: commodity.expected_return,
    }, "commodity");
  };

  const riskColor = (score) => {
    if (score <= 35) return "bg-green-500/10 text-green-400 border-green-500/30";
    if (score <= 55) return "bg-yellow-500/10 text-yellow-400 border-yellow-500/30";
    return "bg-red-500/10 text-red-400 border-red-500/30";
  };

  return (
    <div className="space-y-4">
      {/* Header & Filters */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48" data-testid="commodity-category-filter">
              <SelectValue placeholder={t("opps.categoryFilter")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("credits.allTypes")}</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-xs">
            {filtered.length} / {commodities.length}
          </Badge>
        </div>
        <Button variant="outline" size="sm" onClick={fetchCommodities} disabled={loading} data-testid="commodity-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? t("common.loading") : t("opps.refresh")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["precious_metals", "energy", "agriculture", "industrial_metals"].map((cat) => {
          const Icon = CATEGORY_ICONS[cat];
          const items = commodities.filter((c) => c.category === cat);
          return (
            <Card key={cat} className="cursor-pointer hover:border-primary/50 transition-all" onClick={() => setCategoryFilter(cat === categoryFilter ? "all" : cat)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${CATEGORY_COLORS[cat]}`} />
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    {cat.replace("_", " ")}
                  </span>
                </div>
                <p className="text-xl font-bold">{items.length}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="commodity-table">
              <thead>
                <tr className="border-b border-border/50 bg-muted/30">
                  <SortableHeader label={t("opps.name")} sortKey="name" sort={sort} setSort={setSort} />
                  <SortableHeader label={t("opps.category")} sortKey="category" sort={sort} setSort={setSort} />
                  <SortableHeader label={t("opps.price")} sortKey="price" sort={sort} setSort={setSort} />
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Unit</th>
                  <SortableHeader label={t("opps.riskScore")} sortKey="risk_score" sort={sort} setSort={setSort} />
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">{t("opps.expectedReturn")}</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Source</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const Icon = CATEGORY_ICONS[c.category] || Gem;
                  return (
                    <tr key={c.id} className="border-b border-border/20 hover:bg-muted/20 transition-colors" data-testid={`commodity-row-${c.id}`}>
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${CATEGORY_COLORS[c.category]}`} />
                          {c.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {c.category.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                      </td>
                      <td className="px-4 py-3 font-mono font-bold">
                        ${Math.round(c.price).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{c.unit}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={riskColor(c.risk_score)}>
                          {c.risk_score}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3 text-green-400" />
                          <span className="text-green-400 font-medium">{c.expected_return}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={c.source === "alpha_vantage" ? "text-green-400 border-green-500/30" : "text-muted-foreground"}>
                          {c.source === "alpha_vantage" ? "Live" : "Est."}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!isDemo && (
                          <Button size="sm" variant="outline" onClick={() => handleSave(c)} data-testid={`commodity-save-${c.id}`}>
                            <Tag className="w-3 h-3 mr-1" /> Add to MyDeals
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {sorted.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                {loading ? t("common.loading") : "No commodities found"}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
